import { Positions } from '../../Api'
import { PlayerDataGQL, PlayersAllSeasonStatsData } from '../../Api/Players/player.types'
import {
  COMPLIMENTARY_STATS,
  FilteredResult,
  fullNameStatLabels,
  matchupStatFieldsList,
  offenseStatHeaderList,
  positionSeasonStatHeadersLists,
  positionStatHeaderLists,
  statFields,
  StatListType,
  StatMapType,
  StatTypes,
} from '../../Constants/statsProcess'
import { StatCardProps } from '../../Pages/Player/components/stats/currentSeason/statCard/types'
import { ConvertStatValue, Decimal1Digit, Percent1Digit } from '../formatting'
/**
 * Converts stat field to Human readable full stat name using `fullNameStatLabels`
 */
export const mapPlayerStat = (stat: string): string => {
  return fullNameStatLabels[stat] || ''
}

/**
 * Get stats mapping for player position
 */
export const getMapForPosition = (position?: Positions): StatListType => {
  if (!position) return offenseStatHeaderList

  if (Object.prototype.hasOwnProperty.call(positionStatHeaderLists, position))
    return positionStatHeaderLists[position]
  return offenseStatHeaderList
}

/**
 * Get season stats mapping for player position
 */
export const mapPlayerSeasonStatHeaders = (position?: string) => {
  if (!position) return positionSeasonStatHeadersLists['F']
  if (Object.prototype.hasOwnProperty.call(positionSeasonStatHeadersLists, position))
    return positionSeasonStatHeadersLists[position]
  return positionSeasonStatHeadersLists['F']
}
export function getStatList(position: Positions, statType: StatTypes): StatMapType[] {
  const maps = getMapForPosition(position)[statType]
  const result: StatMapType[] = []
  maps.map((key) => {
    const row = statFields[key]
    if (row) {
      result.push({
        name: row[0],
        longName: row[1],
        field: row[2],
      })
    }
  })
  return result
}

export const getMatchupStatList = (position: Positions) => {
  if (!position) return matchupStatFieldsList['F']
  if (Object.prototype.hasOwnProperty.call(matchupStatFieldsList, position))
    return matchupStatFieldsList[position]
  return matchupStatFieldsList['F']
}

export function extractStatFromObject(
  position: Positions,
  statType: StatTypes,
  data: any | null
): FilteredResult[] {
  if (!data) return []
  const fields = getStatList(position, statType)
  return fields.map((f) => ({
    ...f,
    value: ConvertStatValue(data[f.field], f.field),
  }))
}

export const determineStatAvg = (stat: string, statList: any) => {
  if (statList.length < 1 || stat === 'foRecord') return '-'
  if (stat.includes('Pct')) return getPctAvg(stat, statList)
  return getStatAvg(stat, statList)
}

export const getStatAvg = (stat: string, statList: any) => {
  const total = statList.reduce((acc: any, szn: { [x: string]: any }) => {
    const value = szn[stat as keyof PlayersAllSeasonStatsData]
    let numVal = typeof value === 'string' ? parseFloat(value) : value
    return acc + (numVal || 0)
  }, 0)
  let avg = ConvertStatValue(total / statList.length, stat)
  return typeof avg === 'string' ? avg : Decimal1Digit(avg)
}

export const aggregateTotalStats = (stat: string, statList: any) => {
  const total = statList.reduce((acc: any, szn: { [x: string]: any }) => {
    const value = szn[stat as keyof PlayersAllSeasonStatsData]
    let numVal = typeof value === 'string' ? parseFloat(value) : value
    return acc + (numVal || 0)
  }, 0)
  return typeof total === 'string' ? parseFloat(total) : total
}

export const getPctAvg = (stat: string, statList: any) => {
  const percentageCalculators: Record<string, () => string> = {
    savePct: () => {
      const saves = aggregateTotalStats('saves', statList)
      const ga = aggregateTotalStats('goalsAgainst', statList)
      if (!saves) return '-'
      return Percent1Digit(saves / (saves + ga))
    },
    faceoffPct: () => {
      const faceoffs = aggregateTotalStats('faceoffs', statList)
      const faceoffWins = aggregateTotalStats('faceoffsWon', statList)
      if (!faceoffs) return '-'
      return Percent1Digit(faceoffWins / faceoffs)
    },
    shotPct: () => {
      const shots = aggregateTotalStats('shots', statList)
      const goals = aggregateTotalStats('goals', statList)
      if (!shots) return '-'
      return Percent1Digit(goals / shots)
    },
    shotsOnGoalPct: () => {
      const shots = aggregateTotalStats('shots', statList)
      const shotsOnGoal = aggregateTotalStats('shotsOnGoal', statList)
      if (!shots) return '-'
      return Percent1Digit(shotsOnGoal / shots)
    },
    twoPointShotPct: () => {
      const twoPtSh = aggregateTotalStats('twoPointShots', statList)
      const twoPtG = aggregateTotalStats('twoPointGoals', statList)
      if (!twoPtSh) return '-'
      return Percent1Digit(twoPtG / twoPtSh)
    },
  }

  if (!Object.prototype.hasOwnProperty.call(percentageCalculators, stat)) return '-'
  return percentageCalculators[stat]?.() || '-'
}

/**
 * Get complementary stats for a given stat key
 */
export const getComplementaryStats = (statKey: string, stats: FilteredResult[]) => {
  const statField = Object.entries(statFields).find(([_, value]) => value[0] === statKey)
  if (!statField) return null
  const longStatKey = statField?.[1][2]
  const complementaryStatKeys =
    longStatKey in COMPLIMENTARY_STATS ? COMPLIMENTARY_STATS[longStatKey] : null
  if (!complementaryStatKeys) return null

  return complementaryStatKeys.map((compStatKey) => ({
    name: compStatKey,
    value: stats.find((stat) => stat.field === compStatKey)?.value ?? 0,
  }))
}

/**
 * Calculate core stats for a given player with season stats
 */
export const calculateCoreStats = (
  player: PlayerDataGQL | null | undefined,
  seasonStats: FilteredResult[]
): StatCardProps[] => {
  if (!player || !seasonStats) return []
  const gamesPlayed = seasonStats.find((stat) => stat.name === 'GP')?.value ?? 0
  if( typeof gamesPlayed === 'number' && gamesPlayed < 1) return []

  const coreStatObj =
    player?.position in positionStatHeaderLists
      ? positionStatHeaderLists[player.position]
      : null

  if (coreStatObj) {
    const coreStatList = Array.isArray(coreStatObj.core) ? coreStatObj.core : []

    const cs = coreStatList.map((statKey) => {
      const complementaryStatsData = getComplementaryStats(statKey, seasonStats)
      const statValue = seasonStats.find((stat) => stat.name === statKey)?.value ?? 0

      return {
        statName: statKey,
        statValue: statValue,
        complimentaryStats: complementaryStatsData,
      }
    })
    return cs
  }
  return []
}
